import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import '../GL/LoanPayment.css';
import { formatDateToDDMMYYYY, getTodayYYYYMMDD } from '../../utils/dateUtils';
import { maskAadhaar, maskPAN } from '../../utils/dataMaskingUtils';

const toLocalYYYYMMDD = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addDaysYmd = (ymd, days) => {
  const [y, m, d] = String(ymd || '').slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '';
  const dt = new Date(Date.UTC(y, m - 1, d + Number(days || 0)));
  return dt.toISOString().slice(0, 10);
};

const diffDaysYmd = (fromYmd, toYmd) => {
  const [fy, fm, fd] = String(fromYmd || '').slice(0, 10).split('-').map(Number);
  const [ty, tm, td] = String(toYmd || '').slice(0, 10).split('-').map(Number);
  if (!fy || !ty) return 0;
  return Math.floor((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / (1000 * 60 * 60 * 24));
};

const paidTotalsByDate = (txs) => {
  const map = {};
  (txs || []).forEach((tx) => {
    const key = toLocalYYYYMMDD(tx.payment_date);
    if (!key) return;
    map[key] = (map[key] || 0) + (Number(tx.amount) || 0);
  });
  return map;
};

const TBDLoanPayment = () => {
  const { type, id } = useParams();
  const searchType = type || 'name';
  const [loanDetails, setLoanDetails] = useState(null);
  const [paidDates, setPaidDates] = useState([]);
  const [overdueDetails, setOverdueDetails] = useState([]);
  const [overduePaidByDate, setOverduePaidByDate] = useState({});
  const [showOverdueDetails, setShowOverdueDetails] = useState(false);
  const [overdueInterestRate, setOverdueInterestRate] = useState(0.5); // Default 0.5% per day
  const [totalOverdueInput, setTotalOverdueInput] = useState('');
  const [selectedPaymentDate, setSelectedPaymentDate] = useState(getTodayYYYYMMDD());
  const [paymentMode, setPaymentMode] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [isPaymentSuccessful, setIsPaymentSuccessful] = useState(false);
  const [dailyCollectionAmount, setDailyCollectionAmount] = useState(0);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [showSlipModal, setShowSlipModal] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [remainingForDate, setRemainingForDate] = useState(null); // { remaining, already_paid, expected_daily, is_fully_paid }

  useEffect(() => {
    const fetchLoanDetails = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/tbd-loan-reciever`, {
          params: { type: searchType, value: id },
        });
        setLoanDetails(response.data);
        
        // Prefer stored daily_collection; fall back to loan_amount / period
        if (response.data) {
          const period = Number(response.data.period) || 0;
          const fromField = Number(response.data.daily_collection);
          const computed =
            period > 0 ? Number(response.data.loan_amount) / period : 0;
          const dailyAmount =
            Number.isFinite(fromField) && fromField > 0
              ? fromField
              : Number.isFinite(computed) && computed > 0
                ? computed
                : 0;
          setDailyCollectionAmount(dailyAmount);
          setPaymentAmount(dailyAmount ? dailyAmount.toFixed(2) : '');
        }
      } catch (error) {
        console.error('Error fetching loan details:', error);
      }
    };

    fetchLoanDetails();
  }, [id, searchType]);

  useEffect(() => {
    if (loanDetails && loanDetails.account_no) {
      const fetchPaidDates = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/tbd-fetch-paid-dates/${loanDetails.account_no}`);
          const normalized = (response.data || [])
            .map((d) => toLocalYYYYMMDD(d))
            .filter(Boolean);
          setPaidDates(normalized);
        } catch (error) {
          console.error('Error fetching paid dates:', error);
        }
      };

      const fetchTransactions = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/tbd-daily-transactions/${loanDetails.account_no}`);
          setTransactions(response.data || []);
        } catch (error) {
          console.error('Error fetching transactions:', error);
        }
      };

      const fetchOverduePaid = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/tbd-overdue-paid/${loanDetails.account_no}`);
          const paidMap = {};
          (response.data || []).forEach((row) => {
            const key = toLocalYYYYMMDD(row.payment_date);
            if (key) paidMap[key] = Number(row.overdue_paid) || 0;
          });
          setOverduePaidByDate(paidMap);
        } catch (error) {
          console.error('Error fetching overdue paid amounts:', error);
          setOverduePaidByDate({});
        }
      };

      fetchPaidDates();
      fetchTransactions();
      fetchOverduePaid();
    }
  }, [loanDetails]);

  const refreshOverduePaid = async () => {
    if (!loanDetails?.account_no) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/tbd-overdue-paid/${loanDetails.account_no}`);
      const paidMap = {};
      (response.data || []).forEach((row) => {
        const key = toLocalYYYYMMDD(row.payment_date);
        if (key) paidMap[key] = Number(row.overdue_paid) || 0;
      });
      setOverduePaidByDate(paidMap);
    } catch (error) {
      console.error('Error refreshing overdue paid amounts:', error);
    }
  };

  const buildLocalOverdueDetails = (paidMap = overduePaidByDate, txs = transactions) => {
    const period = Number(loanDetails?.period) || 0;
    const dailyAmount = Number(dailyCollectionAmount) || 0;
    const loanDate = toLocalYYYYMMDD(loanDetails?.date);
    if (!loanDate || period <= 0 || dailyAmount <= 0) return [];

    const paidByDate = paidTotalsByDate(txs);
    const today = getTodayYYYYMMDD();
    const firstCollectionDate = addDaysYmd(loanDate, 1);
    const rateToUse =
      overdueInterestRate === '' || overdueInterestRate == null
        ? 0.5
        : Number(overdueInterestRate);
    const dailyRate = Number.isFinite(rateToUse) ? rateToUse : 0.5;
    const details = [];

    for (let i = 0; i < period; i++) {
      const dueDate = addDaysYmd(firstCollectionDate, i);
      if (!dueDate || dueDate >= today) continue;

      const alreadyPaid = paidByDate[dueDate] || 0;
      const remainingDaily = Math.max(dailyAmount - alreadyPaid, 0);
      if (remainingDaily <= 0.009) continue;

      const daysOverdue = Math.max(0, diffDaysYmd(dueDate, today));
      if (daysOverdue <= 0) continue;

      details.push({
        dueDate,
        dailyAmount: Number(dailyAmount.toFixed(2)),
        amount_paid: Number(alreadyPaid.toFixed(2)),
        remaining_daily: Number(remainingDaily.toFixed(2)),
        daysOverdue,
        overdueInterest: ((dailyRate / 100) * daysOverdue * dailyAmount).toFixed(2),
        overdue_paid: Number(paidMap[dueDate] || 0),
      });
    }
    return details;
  };

  // Fetch remaining balance for selected payment date (allows multiple payments per day)
  useEffect(() => {
    if (!loanDetails?.account_no || !selectedPaymentDate) {
      setRemainingForDate(null);
      return;
    }
    const fetchRemaining = async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/tbd-daily-remaining-for-date/${loanDetails.account_no}/${selectedPaymentDate}`
        );
        setRemainingForDate(res.data);
      } catch (e) {
        setRemainingForDate(null);
      }
    };
    fetchRemaining();
  }, [loanDetails?.account_no, selectedPaymentDate]);

  // Fetch overdue details from backend (avoids timezone / paid-date mismatches)
  useEffect(() => {
    if (!loanDetails?.account_no) {
      setOverdueDetails([]);
      return;
    }

    const fetchOverdueDetails = async () => {
      try {
        const rateToUse =
          overdueInterestRate === '' || overdueInterestRate == null
            ? 0.5
            : Number(overdueInterestRate);
        const response = await axios.get(
          `${API_BASE_URL}/tbd-overdue-details/${loanDetails.account_no}`,
          { params: { rate: Number.isFinite(rateToUse) ? rateToUse : 0.5 } }
        );
        const details = Array.isArray(response.data?.details) ? response.data.details : [];
        setOverdueDetails(details.length ? details : buildLocalOverdueDetails());

        if (response.data?.daily_amount != null && Number(response.data.daily_amount) > 0) {
          setDailyCollectionAmount(Number(response.data.daily_amount));
        }

        const paidMap = {};
        details.forEach((detail) => {
          const key = toLocalYYYYMMDD(detail.dueDate);
          if (key) paidMap[key] = Number(detail.overdue_paid) || 0;
        });
        // Keep any already-fetched overdue-paid amounts for dates not in pending list
        setOverduePaidByDate((prev) => ({ ...prev, ...paidMap }));
      } catch (error) {
        console.error('Error fetching TBD overdue details:', error);
        setOverdueDetails(buildLocalOverdueDetails());
      }
    };

    fetchOverdueDetails();
  }, [loanDetails?.account_no, overdueInterestRate, paidDates, transactions, dailyCollectionAmount]);
  

  // Handle overdue payment submission
  const handleOverduePayment = async (detail) => {
    try {
      await axios.post(`${API_BASE_URL}/tbd-overdue-payment`, {
        account_no: loanDetails.account_no,
        debtor_name: loanDetails.loanee_name,
        overdue_paid: detail.overdueInterest,
        payment_date: detail.dueDate,
        daily_amount: detail.dailyAmount,
        days_overdue: detail.daysOverdue,
      });
      alert('Overdue Payment Successful!');
      const response = await axios.get(`${API_BASE_URL}/tbd-fetch-paid-dates/${loanDetails.account_no}`);
      setPaidDates((response.data || []).map((d) => toLocalYYYYMMDD(d)).filter(Boolean));
      await refreshOverduePaid();
      // paidDates change triggers overdue details refresh
    } catch (error) {
      console.error('Error processing overdue payment:', error);
      alert('Overdue Payment failed. Please try again.');
    }
  };

  const getTotalOverdueInterest = () =>
    overdueDetails.reduce((sum, detail) => sum + (Number(detail.overdueInterest) || 0), 0);

  useEffect(() => {
    setTotalOverdueInput(getTotalOverdueInterest().toFixed(2));
  }, [overdueDetails]);

  const handlePayTotalOverdue = async () => {
    const amountToPay = Number(totalOverdueInput);
    if (!amountToPay || amountToPay <= 0) {
      alert('Please enter a valid total overdue amount.');
      return;
    }

    let remaining = amountToPay;
    const oldestFirst = [...overdueDetails]
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

    try {
      for (const detail of oldestFirst) {
        if (remaining <= 0) break;
        const interestForDate = Number(detail.overdueInterest) || 0;
        if (interestForDate <= 0) continue;

        const payNow = Math.min(remaining, interestForDate);
        await axios.post(`${API_BASE_URL}/tbd-overdue-payment`, {
          account_no: loanDetails.account_no,
          debtor_name: loanDetails.loanee_name,
          overdue_paid: payNow.toFixed(2),
          payment_date: detail.dueDate,
          daily_amount: detail.dailyAmount,
          days_overdue: detail.daysOverdue,
        });
        remaining -= payNow;
      }

      alert('Total overdue payment successful.');
      const response = await axios.get(`${API_BASE_URL}/tbd-fetch-paid-dates/${loanDetails.account_no}`);
      setPaidDates((response.data || []).map((d) => toLocalYYYYMMDD(d)).filter(Boolean));
      await refreshOverduePaid();
    } catch (error) {
      console.error('Error paying total overdue:', error);
      alert('Failed to pay total overdue. Please try again.');
    }
  };

  const refreshOverdueDetails = async () => {
    if (!loanDetails?.account_no) return;
    try {
      const rateToUse =
        overdueInterestRate === '' || overdueInterestRate == null
          ? 0.5
          : Number(overdueInterestRate);
      const odRes = await axios.get(
        `${API_BASE_URL}/tbd-overdue-details/${loanDetails.account_no}`,
        { params: { rate: Number.isFinite(rateToUse) ? rateToUse : 0.5 } }
      );
      const details = Array.isArray(odRes.data?.details) ? odRes.data.details : [];
      setOverdueDetails(details.length ? details : buildLocalOverdueDetails());
    } catch (error) {
      console.error('Error refreshing TBD overdue details:', error);
      setOverdueDetails(buildLocalOverdueDetails());
    }
  };

  // Handle daily payment submission
  const handlePayment = async () => {
    // Prevent double-clicks
    if (isProcessingPayment) {
      return;
    }

    if (!selectedPaymentDate || !paymentMode) {
      alert('Please fill in payment date and mode.');
      return;
    }
    const amount = Number(paymentAmount);
    if (!amount || amount <= 0) {
      alert('Enter a valid payment amount.');
      return;
    }

    setIsProcessingPayment(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/tbd-daily-payment`, {
        account_no: loanDetails.account_no,
        payment_date: selectedPaymentDate,
        payment_mode: paymentMode,
        transaction_id: transactionId.trim() || null,
        daily_amount: amount,
        loanee_name: loanDetails.loanee_name,
      });
      // Show success message with excess payment info if applicable
      const successMessage = response.data?.message || 'Daily Payment Successful!';
      alert(successMessage);
      setIsPaymentSuccessful(true);
      setSelectedPaymentDate(getTodayYYYYMMDD());
      setPaymentMode('');
      setTransactionId('');
      setPaymentAmount(dailyCollectionAmount.toFixed(2));
      
      // Refresh paid dates and transactions
      const [paidDatesRes, transactionsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/tbd-fetch-paid-dates/${loanDetails.account_no}`),
        axios.get(`${API_BASE_URL}/tbd-daily-transactions/${loanDetails.account_no}`)
      ]);
      setPaidDates((paidDatesRes.data || []).map((d) => toLocalYYYYMMDD(d)).filter(Boolean));
      setTransactions(transactionsRes.data || []);
      await refreshOverdueDetails();
    } catch (error) {
      const errMsg = error.response?.data?.message || error.response?.data?.error;
      if (error.response?.data?.error === 'Already Fully Paid' || errMsg?.includes('fully paid')) {
        alert(error.response?.data?.message || 'This date is already fully paid. No remaining balance.');
      } else if (error.response?.data?.error === 'Duplicate Entry' || errMsg?.includes('unique constraint')) {
        alert(error.response?.data?.message || 'Multiple payments per day require a database migration. Please contact support.');
      } else if (error.response && error.response.data.error === 'Already Paid') {
        alert('Payment for this date is already made');
      } else {
        console.error('Error processing payment:', error);
        alert('Payment failed. Please try again.');
      }
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Generate and print payment slip
  const generateSlip = (opts = {}) => {
    const { payment } = opts; // payment: { amount, payment_date, payment_mode, transaction_id }
    const amountToShow = payment?.amount ?? (Number(paymentAmount) || dailyCollectionAmount);
    const dateToShow = payment?.payment_date ? new Date(payment.payment_date) : new Date(selectedPaymentDate);
    const modeToShow = payment?.payment_mode || paymentMode || '';
    const txToShow = payment?.transaction_id || transactionId || '';

    const slipContent = `
  
<style>
  body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
  .container { display: flex; }
  .left-receipt { width: 40%; border: 1px solid #000; padding: 15px; box-sizing: border-box; }
  .right-receipt { width: 60%; border: 1px solid #000; padding: 15px; box-sizing: border-box; }
  .header { text-align: center; font-weight: bold; font-size: 1.2em; margin-bottom: 5px; }
  .sub-header { text-align: center; font-size: 0.9em; margin-bottom: 15px; }
  .details { margin: 10px 0; }
  .line { display: flex; justify-content: space-between; margin: 5px 0; }
  .table { width: 100%; border-collapse: collapse; margin-top: 10px; }
  .table td { padding: 5px; }
  .table .right { text-align: right; font-weight: bold; }
  .total { border-top: 1px solid #000; margin-top: 10px; padding-top: 5px; font-weight: bold; }
  .footer { text-align: center; margin-top: 15px; font-size: 0.9em; }
</style>

<div class="container">
  <!-- Left Receipt -->
  <div class="left-receipt">
    <div class="header">SHREE DHANESHWARI FINANCE AND LEASING</div>
    <div class="sub-header">Kasgi , Osmanabad</div>
    <div class="details">
      <div class="line"><strong>A/C No:</strong> ${loanDetails.account_no}</div>
      <div class="line"><strong>Date:</strong> ${formatDateToDDMMYYYY(new Date())}</div>
    </div>
    <div class="details">
    <div class="line"><strong>Received from Sri:</strong> ${loanDetails.loanee_name}</div>
    <div class="line"><strong>Daily Collection Amount Rs:</strong> ₹${Number(amountToShow).toFixed(2)}</div>
    <div class="line"><strong>Payment Date:</strong> ${formatDateToDDMMYYYY(dateToShow)}</div>
   </div>
    <table class="table">
        <tr>
          <td>DAILY COLLECTION PAID</td>
          <td class="right">${Number(amountToShow).toFixed(2)}</td>
        </tr>
      </table>
      <div class="total">
        TOTAL: ₹${Number(amountToShow).toFixed(2)}
      </div>
    <div class="footer">
      Cashier / Mg. Partner                      
    </div>
  </div>

  <!-- Right Receipt -->
  <div class="right-receipt">
    <div class="header">SHREE DHANESHWARI FINANCE AND LEASING</div>
    <div class="sub-header">Kasgi , Osmanabad</div>
    <div class="details">
      <div class="line"><strong>A/C No:</strong> ${loanDetails.account_no}</div>
      <div class="line"><strong>Date:</strong> ${formatDateToDDMMYYYY(new Date())}</div>
    </div>
   <div class="details">
     
      <div class="line"><strong>Received from Sri:</strong> ${loanDetails.loanee_name}</div>
      <div class="line"><strong>Daily Collection Amount Rs:</strong> ₹${Number(amountToShow).toFixed(2)}</div>
      <div class="line"><strong>Payment Date:</strong> ${formatDateToDDMMYYYY(dateToShow)}</div>
    </div>
    <table class="table">
      <tr>
          <td>TBD Interest A/c</td>
          <td class="right">${Number(amountToShow).toFixed(2)}</td>
        </tr>
      </table>
      <div class="total">
        TOTAL: ₹${Number(amountToShow).toFixed(2)}
      </div>
    </table>
    <div class="footer">
      Cashier / Mg. Partner
    </div>
  </div>
</div>
`;
  const printWindow = window.open('', '', 'height=800,width=1200');
  printWindow.document.write('<html><head><title>Interest Slip</title></head><body>');
  printWindow.document.write(slipContent);
  printWindow.document.write('</body></html>');
  printWindow.document.close();
  printWindow.print();
};

  // Toggle overdue details table on Ctrl + B key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'b') {
        setShowOverdueDetails((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!loanDetails) {
    return <h2>Loading loan details...</h2>;
  }

  const paidByDateMap = paidTotalsByDate(transactions);
  const expectedDaily = Number(dailyCollectionAmount) || 0;
  const paidDaysCount = Object.values(paidByDateMap).filter(
    (sum) => expectedDaily <= 0 || sum >= expectedDaily - 0.009
  ).length || paidDates.length;
  const remainingDaysCount = Math.max(0, Number(loanDetails.period || 0) - paidDaysCount);
  const totalAmountPaid = transactions.reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);

  return (
    <div className="loan-payment-container">
      <div className="loan-payment-header">TBD Daily Collection Payment</div>
      {/* Loan Details */}
      <div className="loan-details">
        <p><strong>Account No:</strong> {loanDetails.account_no}</p>
        <p><strong>Loanee Name:</strong> {loanDetails.loanee_name}</p>
        <p><strong>Date:</strong> {formatDateToDDMMYYYY(loanDetails.date)}</p>
        <p><strong>Aadhaar Debtor:</strong> {maskAadhaar(loanDetails.aadhaar_debtor)}</p>
        <p><strong>PAN Card:</strong> {maskPAN(loanDetails.pan_card)}</p>
        <p><strong>Address Debtor:</strong> {loanDetails.address_debtor}</p>
        <p><strong>Guarantor Name:</strong> {loanDetails.guarantor_name}</p>
        <p><strong>Loan Amount:</strong> ₹{loanDetails.loan_amount}</p>
        <p><strong>Period:</strong> {loanDetails.period} days</p>
        <p><strong>Daily Collection Amount:</strong> ₹{dailyCollectionAmount.toFixed(2)}</p>
        <p><strong>Collector Name:</strong> {loanDetails.collector_name || 'Not specified'}</p>
      </div>
      {/* Payment History */}
      <div className="payment-history">
        <h3>Payment History</h3>
        <p><strong>Total Paid Days:</strong> {paidDaysCount} out of {loanDetails.period} days</p>
        <p><strong>Remaining Days:</strong> {remainingDaysCount} days</p>
        <p><strong>Total Amount Paid:</strong> ₹{totalAmountPaid.toFixed(2)}</p>
        <p><strong>Remaining Amount:</strong> ₹{Math.max(0, (Number(loanDetails.loan_amount) - totalAmountPaid)).toFixed(2)}</p>
      </div>
      {/* Overdue Details */}
      {showOverdueDetails && (
        <div className="overdue-details">
          <h3 className="overdue-title">Overdue Details (Press Ctrl+B to toggle)</h3>
          <div className="overdue-summary-row">
            <div className="overdue-card">
              <label>Overdue Interest Rate (% per day):</label>
              <input
                type="number"
                step="0.1"
                value={overdueInterestRate}
                onChange={(e) => setOverdueInterestRate(e.target.value)}
              />
            </div>
            <div className="overdue-card">
              <label>Total Overdue Interest:</label>
              <div className="total-overdue-input-group">
                <input
                  type="number"
                  step="0.01"
                  value={totalOverdueInput}
                  onChange={(e) => setTotalOverdueInput(e.target.value)}
                />
                <button type="button" onClick={handlePayTotalOverdue}>Pay Total Overdue</button>
              </div>
              <p className="overdue-note">
                Default total (₹{getTotalOverdueInterest().toFixed(2)}). Edit to pay a different amount; it will be distributed to pending overdue dates (oldest first).
              </p>
            </div>
          </div>
          <table className="overdue-table">
            <thead>
              <tr>
                <th>Due Date</th>
                <th>Daily Amount</th>
                <th>Days Overdue</th>
                <th>Overdue Interest</th>
                <th>Overdue Paid</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {overdueDetails.map((detail, index) => (
                <tr key={detail.dueDate || index}>
                  <td>{formatDateToDDMMYYYY(detail.dueDate)}</td>
                  <td>₹{parseFloat(detail.dailyAmount).toFixed(2)}</td>
                  <td>{detail.daysOverdue}</td>
                  <td>₹{detail.overdueInterest}</td>
                  <td>₹{(Number(overduePaidByDate[detail.dueDate] ?? detail.overdue_paid) || 0).toFixed(2)}</td>
                  <td>
                    <button onClick={() => handleOverduePayment(detail)}>Pay Overdue</button>
                  </td>
                </tr>
              ))}
              {overdueDetails.length === 0 && (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '16px', color: '#666' }}>
                    No pending overdue dates found for this account.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {/* Payment Form */}
      <div className="payment-form">
        <h3>Make Daily Payment</h3>
        <div>
          <label>Payment Date:</label>
          <input
            type="date"
            value={selectedPaymentDate}
            onChange={(e) => setSelectedPaymentDate(e.target.value)}
          />
        </div>
        <div>
          <label>Daily Collection Amount:</label>
          <input
            type="number"
            step="0.01"
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
            placeholder="Enter amount"
          />
          {remainingForDate?.remaining > 0 && (
            <button
              type="button"
              style={{ marginLeft: '8px', fontSize: '12px' }}
              onClick={() => setPaymentAmount(remainingForDate.remaining.toFixed(2))}
            >
              Fill remaining (₹{remainingForDate.remaining.toFixed(2)})
            </button>
          )}
          {selectedPaymentDate && (() => {
            const expectedAmount = dailyCollectionAmount;
            const amount = Number(paymentAmount) || 0;
            const excess = amount > expectedAmount ? amount - expectedAmount : 0;
            const remaining = remainingForDate?.remaining ?? null;
            const isFullyPaid = remainingForDate?.is_fully_paid;
            return (
              <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                <div>Expected daily amount: ₹{expectedAmount.toFixed(2)}</div>
                {remaining != null && remaining > 0 && (
                  <div style={{ color: '#0066cc', fontWeight: 'bold', marginTop: '3px' }}>
                    Remaining for this date: ₹{remaining.toFixed(2)} — you can pay again on this date to clear the balance.
                  </div>
                )}
                {remaining != null && isFullyPaid && (
                  <div style={{ color: '#28a745', marginTop: '3px' }}>This date is fully paid.</div>
                )}
                {excess > 0 && (
                  <div style={{ color: '#28a745', fontWeight: 'bold', marginTop: '3px' }}>
                    Excess ₹{excess.toFixed(2)} will be applied to next day's collection
                  </div>
                )}
              </div>
            );
          })()}
        </div>
        <div>
  <label>Payment Mode:</label>
  <select
    value={paymentMode}
    onChange={(e) => setPaymentMode(e.target.value)}
  >
    <option value="">Select</option>
    <option value="Cash">Cash</option>
    <option value="UPI">UPI</option>
    <option value="Cheque">Cheque</option>
  </select>
</div>
<div>
          <label>Transaction ID (Optional):</label>
          <input
            type="text"
            value={transactionId}
            onChange={(e) => setTransactionId(e.target.value)}
            placeholder="Enter Transaction ID (Optional)"
          />
        </div>
        <button onClick={handlePayment} disabled={isProcessingPayment}>
          {isProcessingPayment ? 'Processing...' : 'Submit Daily Payment'}
        </button>
      </div>

      {/* Slip Button */}
      {isPaymentSuccessful && (
        <button onClick={() => generateSlip()} className="slip-button">Generate Slip</button>
      )}

      {/* View All Slips Modal Trigger */}
      <div style={{ marginTop: 10 }}>
        <button onClick={() => setShowSlipModal(true)}>View All Payment Slips</button>
      </div>

      {showSlipModal && (
        <div className="modal-overlay" onClick={() => setShowSlipModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Payment Slips</h3>
              <button className="modal-close" onClick={() => setShowSlipModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {(transactions || []).map((tx, idx) => (
                <div key={idx} className="slip-row">
                  <div><strong>Date:</strong> {tx.payment_date ? formatDateToDDMMYYYY(tx.payment_date) : '-'}</div>
                  <div><strong>Mode:</strong> {tx.payment_mode}</div>
                  <div><strong>Txn:</strong> {tx.transaction_id || '-'}</div>
                  <div><strong>Amount:</strong> ₹{Number(tx.amount).toFixed(2)}</div>
                  <div>
                    <button onClick={() => generateSlip({ payment: tx })}>Print</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TBDLoanPayment;
